import { DeliveryNote } from "Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { DeliveryNoteLinkRequest } from "@finway-group/shared/lib/models/expense/linkRequest.interface"
import { useEffect, useMemo, useState } from "react"
import { diff } from "Shared/utils/array.utils"
import { useLinkDeliveryNotePurchaseOrderMutation } from "Features/pages/deliveryNotes/queries/deliveryNote.queries"
import { PurchaseOrder } from "Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { PurchaseOrderStatusEnum } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatus.enum"
import { decorateItemsWithIndex } from "../PurchaseOrderLink/useLinkPurchaseOrder.hook"

export type LinkedPair = DeliveryNoteLinkRequest["links"][number]

const pairSortKey = (pair: LinkedPair) => `${pair.deliveryNoteItemId},${pair.poItemId},${pair.poId}`

export const useLinkDeliveryNote = (deliveryNote: DeliveryNote | undefined, purchaseOrders: Array<Pick<PurchaseOrder, "_id" | "state">> = []) => {
    const { mutateAsync: linkPurchaseOrder, isLoading: isLinkMutationLoading } = useLinkDeliveryNotePurchaseOrderMutation()
    const deliveryNoteLineItems = useMemo(() => decorateItemsWithIndex(deliveryNote?.lineItems || []), [deliveryNote?.lineItems])
    const [activeItem, setActiveItem] = useState<string>("")

    const initialLinkedPairs: Array<LinkedPair> = useMemo(
        () =>
            deliveryNoteLineItems
                .filter((item) => item.purchaseOrderLink)
                .map((item) => ({
                    deliveryNoteItemId: item._id!,
                    poId: item.purchaseOrderLink?._id!,
                    poItemId: item.purchaseOrderLink?.itemId!,
                })),
        [deliveryNoteLineItems],
    )

    const isPoLinkingDisabled = (poId: string): boolean => {
        const po = purchaseOrders.find((purchaseOrder) => purchaseOrder._id === poId)
        if (!po?.state?.currentState) {
            return true
        }
        return po.state.currentState !== PurchaseOrderStatusEnum.FULFILLMENT
    }

    const [linkedPairs, setLinkedPairs] = useState<Array<LinkedPair>>([])

    useEffect(() => {
        if (initialLinkedPairs.length > 0) {
            setLinkedPairs(initialLinkedPairs)
        }
    }, [deliveryNote?._id])

    const hasChanges = useMemo(() => {
        if (linkedPairs.length !== initialLinkedPairs.length) {
            return true
        }
        const differences = diff(linkedPairs, initialLinkedPairs, pairSortKey)
        return differences.hasChanges
    }, [linkedPairs, initialLinkedPairs])

    if (deliveryNoteLineItems.length === 0) {
        return {
            deliveryNoteLineItems,
            handleDeliveryNoteItemClick: () => {},
            handlePurchaseOrderItemClick: () => {},
            isPoLinkingDisabled: () => true,
            removeLink: () => {},
            clearAllLinks: () => {},
            isDeliveryNoteItemLinked: () => false,
            isPurchaseOrderItemLinked: () => false,
            isItemActive: () => false,
            isPurchaseOrderItemDisabled: () => false,
            saveLinkChanges: () => {},
            hasChanges: false,
            linkedPairs: [],
            activeItem: null,
            isClearAllDisabled: false,
            isLinkMutationLoading: false,
        }
    }

    const handleDeliveryNoteItemClick = (itemId: string) => {
        const isDeliveryNoteLinked = linkedPairs.some((pair) => pair.deliveryNoteItemId === itemId)
        if (isDeliveryNoteLinked) {
            return
        }
        setActiveItem((prev) => (prev === itemId ? "" : itemId))
    }

    const handlePurchaseOrderItemClick = (itemId: string, poId: string) => {
        if (isPoLinkingDisabled(poId)) {
            return
        }
        const isPurchaseLinked = linkedPairs.some((pair) => pair.poId === poId && pair.poItemId === itemId)
        if (isPurchaseLinked) {
            return
        }

        if (!activeItem) {
            return
        }

        setLinkedPairs((prev) => [...prev, { deliveryNoteItemId: activeItem, poItemId: itemId, poId }])
        setActiveItem("")
    }

    const removeLink = (deliveryNoteItemId: string, purchaseItemId: string, poId: string) => {
        if (isPoLinkingDisabled(poId)) {
            return
        }
        setLinkedPairs((prev) => prev.filter((pair) => !(pair.deliveryNoteItemId === deliveryNoteItemId && pair.poItemId === purchaseItemId)))
    }

    const clearAllLinks = () => {
        // Check if any currently linked POs are completed
        const hasCompletedLinkedPO = linkedPairs.some((pair) => isPoLinkingDisabled(pair.poId))
        if (hasCompletedLinkedPO) {
            return
        }
        setLinkedPairs([])
        setActiveItem("")
    }

    const isDeliveryNoteItemLinked = (itemId: string | undefined) => linkedPairs.some((pair) => pair.deliveryNoteItemId === itemId)

    const isPurchaseOrderItemLinked = (poId: string, poItemId: string | undefined) => linkedPairs.some((pair) => pair.poId === poId && pair.poItemId === poItemId)

    const isItemActive = (itemId: string | undefined) => activeItem === itemId

    const isPurchaseOrderItemDisabled = (poId: string, poItemId: string | undefined) => isPoLinkingDisabled(poId) || (!activeItem && !isPurchaseOrderItemLinked(poId, poItemId))

    const saveLinkChanges = () => {
        const differences = diff(linkedPairs, initialLinkedPairs, pairSortKey)
        if (!differences.hasChanges) {
            return
        }

        return linkPurchaseOrder({
            deliveryNoteId: deliveryNote?._id!,
            linkRequest: {
                unlinks: differences.removed.map((pair) => ({
                    deliveryNoteItemId: pair.deliveryNoteItemId,
                    poId: pair.poId,
                    poItemId: pair.poItemId,
                })),
                links: differences.added.map((pair) => ({
                    deliveryNoteItemId: pair.deliveryNoteItemId,
                    poId: pair.poId,
                    poItemId: pair.poItemId,
                })),
            },
        })
    }

    // Helper to check if clear all should be disabled
    const isClearAllDisabled = linkedPairs.length === 0 || linkedPairs.some((pair) => isPoLinkingDisabled(pair.poId))

    return {
        deliveryNoteLineItems,
        handleDeliveryNoteItemClick,
        handlePurchaseOrderItemClick,
        removeLink,
        clearAllLinks,
        isDeliveryNoteItemLinked,
        isPurchaseOrderItemLinked,
        isItemActive,
        isPurchaseOrderItemDisabled,
        saveLinkChanges,
        hasChanges,
        linkedPairs,
        activeItem,
        isPoLinkingDisabled,
        isClearAllDisabled,
        isLinkMutationLoading,
    }
}
